import { DOCUMENT, isPlatformBrowser } from '@angular/common';
import { Inject, Injectable, PLATFORM_ID } from '@angular/core';
import { ConsentService } from './consent.service';
import { AdUserDataConsent } from './consent.service';

export type DataLayerRecord = Record<string, any>;

export interface BuiltUserData {
    userData: DataLayerRecord;
    /** PDF v4.3: the normalized email SHA-256 is the browser user_id. */
    userId?: string;
}

@Injectable({ providedIn: 'root' })
export class NecDataLayerService {
    private previousAdUserDataConsent: AdUserDataConsent = 'unknown';

    constructor(
        @Inject(PLATFORM_ID) private readonly platformId: object,
        @Inject(DOCUMENT) private readonly document: Document,
        private readonly consentService: ConsentService,
    ) {
        this.consentService.adUserDataConsent$.subscribe((consent) => {
            const consentWasRevoked = this.previousAdUserDataConsent === 'granted'
                && (consent === 'denied' || consent === 'unknown');
            this.previousAdUserDataConsent = consent;

            if (consentWasRevoked) {
                this.clearUserData();
            }
        });
    }

    pushEvent(event: string, payload: DataLayerRecord = {}): void {
        this.push({ event, event_id: this.createEventId(), ...payload });
    }

    pushEcommerce(event: string, ecommerce: DataLayerRecord, payload: DataLayerRecord = {}): void {
        if (!this.isBrowser()) return;
        this.dataLayer().push({ ecommerce: null });
        this.push({ event, event_id: this.createEventId(), ...payload, ecommerce });
    }

    pushPageView(payload: DataLayerRecord): void {
        this.pushEvent('page_view', payload);
    }

    buildItem(item: DataLayerRecord): DataLayerRecord {
        return this.clean(item) as DataLayerRecord;
    }

    /**
     * Builds clear-text and SHA-256 user-data pairs atomically. No caller can
     * create a clear-text-only user_data object through this tracking service.
     */
    async buildUserData(profile: DataLayerRecord): Promise<BuiltUserData | null> {
        if (!this.isBrowser() || !this.consentService.hasAdUserDataConsent()) return null;

        const street = [
            this.trim(profile?.['anmelder_strasse']),
            this.trim(profile?.['anmelder_hausnummer']),
        ].filter(Boolean).join(' ');
        const country = this.countryCode(profile?.['anmelder_land']);
        const phone = this.e164Phone(profile?.['anmelder_telefon']);
        const fields: Array<{ key: string; hashKey: string; value?: string; hashValue: (value: string) => string }> = [
            { key: 'email', hashKey: 'email_sha256', value: this.trim(profile?.['anmelder_email']), hashValue: value => this.normalize(value) },
            { key: 'phone_number', hashKey: 'phone_sha256', value: phone, hashValue: value => value },
            { key: 'first_name', hashKey: 'first_name_sha256', value: this.trim(profile?.['anmelder_firstname']), hashValue: value => this.normalizeName(value) },
            { key: 'last_name', hashKey: 'last_name_sha256', value: this.trim(profile?.['anmelder_lastname']), hashValue: value => this.normalizeName(value) },
            { key: 'street', hashKey: 'street_sha256', value: street || undefined, hashValue: value => this.normalizeWhitespace(value) },
            { key: 'city', hashKey: 'city_sha256', value: this.trim(profile?.['anmelder_wohnort']), hashValue: value => this.normalize(value) },
            { key: 'postal_code', hashKey: 'postal_code_sha256', value: this.trim(profile?.['anmelder_plz']), hashValue: value => this.normalize(value) },
            { key: 'country', hashKey: 'country_sha256', value: country, hashValue: value => this.normalize(value) },
        ];

        try {
            const userData: DataLayerRecord = {};
            for (const field of fields) {
                if (!field.value) continue;
                // Keep each clear-text value and its hash in the same builder pass.
                userData[field.key] = field.value;
                userData[field.hashKey] = await this.sha256(field.hashValue(field.value));
            }

            if (!Object.keys(userData).length) return null;
            return { userData, userId: userData['email_sha256'] };
        } catch {
            // Hashing failures must never result in a partial identity object.
            return null;
        }
    }

    pushUserData(userData: BuiltUserData): boolean {
        if (!this.consentService.hasAdUserDataConsent() || !Object.keys(userData?.userData || {}).length) {
            return false;
        }
        this.push({ user_data: userData.userData, user_id: userData.userId });
        return true;
    }

    clearUserData(): void {
        if (!this.isBrowser()) return;
        // Null is mandatory here: it clears the persistent DataLayer state.
        this.dataLayer().push({ user_data: null });
        // user_id is sibling state, not nested in user_data, so clear it as well.
        this.dataLayer().push({ user_id: null });
    }

    pushPurchase(transactionId: string, ecommerce: DataLayerRecord, payload: DataLayerRecord = {}): boolean {
        if (!this.isBrowser() || !transactionId) return false;
        const storage = this.document.defaultView!.sessionStorage;
        if (storage.getItem('nec_purchase_sent') === transactionId) return false;
        const matchingData = this.consentService.hasAdUserDataConsent() ? payload : {};
        this.pushEcommerce('purchase', { ...ecommerce, transaction_id: transactionId }, matchingData);
        storage.setItem('nec_purchase_sent', transactionId);
        // Clear any state push immediately after the successful conversion push.
        this.clearUserData();
        return true;
    }

    toCents(value: number): number { return Math.round(Number(value) * 100); }
    fromCents(value: number): number { return Number((value / 100).toFixed(2)); }

    createEventId(): string {
        if (!this.isBrowser()) return '';
        return globalThis.crypto?.randomUUID?.() || `${Date.now()}-${Math.random()}`;
    }

    private push(value: DataLayerRecord): void {
        if (!this.isBrowser()) return;
        this.dataLayer().push(this.clean(value));
    }

    private dataLayer(): DataLayerRecord[] {
        const win = this.document.defaultView as any;
        return win.dataLayer = win.dataLayer || [];
    }

    private isBrowser(): boolean { return isPlatformBrowser(this.platformId); }

    private trim(value: unknown): string | undefined {
        if (typeof value !== 'string') return undefined;
        const trimmed = value.trim();
        return trimmed || undefined;
    }

    private normalize(value: string): string {
        return value.normalize('NFKC').trim().toLowerCase();
    }

    private normalizeName(value: string): string {
        return this.normalize(value).replace(/[\s!-\/:-@\[-`{-~]/g, '');
    }

    private normalizeWhitespace(value: string): string {
        return this.normalize(value).replace(/\s+/g, ' ');
    }

    private e164Phone(value: unknown): string | undefined {
        const phone = this.trim(value);
        // The form has no E.164 conversion. Do not guess a country prefix.
        return phone && /^\+[1-9]\d{6,14}$/.test(phone) ? phone : undefined;
    }

    private countryCode(value: unknown): string | undefined {
        const country = this.trim(value);
        return country && /^[a-z]{2}$/i.test(country) ? country.toUpperCase() : undefined;
    }

    private async sha256(value: string): Promise<string> {
        const crypto = this.document.defaultView?.crypto;
        if (!crypto?.subtle) throw new Error('Web Crypto is unavailable');
        const hash = await crypto.subtle.digest('SHA-256', new TextEncoder().encode(value));
        return Array.from(new Uint8Array(hash), byte => byte.toString(16).padStart(2, '0')).join('');
    }

    private clean(value: any): any {
        if (value === null || value === undefined || value === '') return undefined;
        if (Array.isArray(value)) return value.map(v => this.clean(v)).filter(v => v !== undefined);
        if (typeof value !== 'object') return value;
        return Object.fromEntries(Object.entries(value)
            .map(([key, entry]) => [key, this.clean(entry)])
            .filter(([, entry]) => entry !== undefined));
    }
}
